import { useState } from "react";
import { IMAGES_PATH } from "../../../../constants";
import { Box, Card, Grid } from "@mui/material";
import ZoomMeetingModal from "./ZoomMeetingModal";
import MeetingSummary from "./MeetingSummary";
import { appToast } from "../../../../components/notification/app-toast";
import { saveAs } from "file-saver";
import {
  formatDate,
  DATE_FORMATS,
} from "../../../../services/utils/date-and-time.utils";
import RecordingDetails from "./RecordingDetails";

interface RecordingCardProps {
  data: any;
}

export default function RecordingCard({ data }: RecordingCardProps) {
  const [showRecording, setShowRecording] = useState(false);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);

  const recordingDownloadUrl =
    data?.zoomRecordingDetails?.downloadUrls?.video || "";
  const thumbnailUrl =
    data?.thumbnailUrl || `${IMAGES_PATH}/default-logo-img.jpg`;
  const zoomRecordingUrl = data?.zoomRecordingDetails?.recordings || "";
  const downloadUrlsMap = data?.zoomRecordingDetails?.downloadUrls || {};
  const transcriptKey = Object.keys(downloadUrlsMap).find((k) =>
    k.includes("audio_transcript")
  );
  const transcriptDownloadUrl = transcriptKey
    ? downloadUrlsMap[transcriptKey]
    : "";
  const userData = {
    ambassador: data?.ambassador,
    client: data?.client,
  };
  const slot = data?.selectedTimeSlotRaw;

  const getFilenameFromUrl = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      const last = pathname.split("/").filter(Boolean).pop();
      return last || "download";
    } catch {
      const parts = url.split("?")[0].split("/");
      return parts[parts.length - 1] || "download";
    }
  };

  const downloadFile = async (url: string, label: string) => {
    if (!url) {
      appToast.error(`${label} not available for this recording.`);
      return;
    }
    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const filename = getFilenameFromUrl(url);
      saveAs(blob, filename);
    } catch (e) {
      appToast.error(`Failed to download ${label}. Please try again later.`);
    }
  };

  const handleThumbnailImageLoad = () => setIsThumbnailLoaded(true);

  const attendeeLabel = [data?.name, data?.title].filter(Boolean).join(", ");
  const dateFormatted = slot?.from
    ? formatDate(slot.from, DATE_FORMATS.fullDateWithDayWithoutDay)
    : "-";
  const timeFormatted =
    slot?.from && slot?.to
      ? `${formatDate(slot.from, DATE_FORMATS.hourMinWithAMPM)} - ${formatDate(slot.to, DATE_FORMATS.hourMinWithAMPM)}`
      : "-";

  return (
    <>
      <Card sx={{ p: 2, pb: 0 }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            lg={6}
            xl={4.5}
            sx={{
              borderRight: { xs: "none", md: 1 },
              borderColor: { md: "divider" },
              p: 2
            }}
          >
            <RecordingDetails
              thumbnailUrl={thumbnailUrl}
              isThumbnailLoaded={isThumbnailLoaded}
              onThumbnailLoad={handleThumbnailImageLoad}
              onPlayRecording={() => setShowRecording(true)}
              attendeeLabel={attendeeLabel}
              company={data?.company}
              dateFormatted={dateFormatted}
              timeFormatted={timeFormatted}
              recordingDownloadUrl={recordingDownloadUrl}
              transcriptDownloadUrl={transcriptDownloadUrl}
              onDownloadFile={downloadFile}
            />
          </Grid>
          <Grid item xs={12} lg={6} xl={7.5} sx={{ p: { md: 2 } }}>
            <MeetingSummary data={data?.meetingSummary} />
          </Grid>
        </Grid>
      </Card>

      <ZoomMeetingModal
        open={showRecording}
        videoUrl={zoomRecordingUrl}
        vttUrl={transcriptDownloadUrl}
        downloadUrls={downloadUrlsMap}
        summaryData={data?.meetingSummary}
        userData={userData}
        onClose={() => setShowRecording(false)}
      />
    </>
  );
}
